/**
 * `entry.client` factory for brands on React Router 7 framework mode (replaces `app/main.jsx`).
 */
import {hydrate, QueryClient, QueryClientProvider} from '@tanstack/react-query';
import React, {startTransition} from 'react';
import {hydrateRoot} from 'react-dom/client';
import {HydratedRouter} from 'react-router/dom';
import {LogSource} from 'commerceAPI/custom-apis/CustomShopperSystem/ICustomShopperSystem';

import {preloadLazyModules} from 'coreUtils/adapters/lazy';
import {uuidv4} from 'coreUtils/adapters/sdk';

import {AppBootstrap, AppBootstrapContext} from './AppBootstrapContext';
import {MOBIFY_DATA_ELEMENT_ID} from './Document';
import type {AppConfigStatics, AppLocals, FetchTranslations, PreloadedState, TranslationMessages} from './types';

import './reselectDevChecks';

type StartClientOptions = {
	AppConfig: Pick<AppConfigStatics, 'restore'>;
	createQueryClient: () => QueryClient;
	fetchTranslations: FetchTranslations;
	serviceWorkerUrl?: string;
};

const LEGACY_SERVICE_WORKER = 'ServiceWorker.js';
/** Hydration never waits longer than this for the lazy chunks of the page. */
const LAZY_MODULES_PRELOAD_TIMEOUT = 5000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const registerServiceWorker = (url: string) =>
	new Promise((resolve) => window.addEventListener('load', resolve))
		.then(() => navigator.serviceWorker.register(url))
		.catch((err) => console.error('ServiceWorker registration failed', err));

/**
 * Unregisters the SFRA service worker once, then registers the PWA one.
 */
const setupServiceWorker = async (url: string) => {
	if (!('serviceWorker' in navigator)) {
		return;
	}

	const registrations = await navigator.serviceWorker.getRegistrations();
	const legacy = registrations.filter((registration) => registration?.active?.scriptURL.endsWith(LEGACY_SERVICE_WORKER));

	if (legacy.length > 0) {
		await Promise.all(legacy.map((registration) => registration.unregister()));
		navigator.serviceWorker.oncontrollerchange = () => window.location.reload();
	}

	return registerServiceWorker(url);
};

/**
 * Resolves once the whole document is parsed. The entry script of React Router is an `async` module: with the
 * document streamed (`entry.server`) it can run while `#mobify-data`, written at the end, is still arriving.
 */
export const documentParsed = () =>
	document.readyState === 'loading'
		? new Promise<void>((resolve) => document.addEventListener('DOMContentLoaded', () => resolve(), {once: true}))
		: Promise.resolve();

/**
 * Reads the globals serialized by `entry.server` into `#mobify-data` and exposes them on `window`.
 * Only once the document is parsed (see `documentParsed`).
 */
/**
 * Dev server: `entry.server` inlines the CSS of the lazy pages (`DEV_LAZY_CSS_ATTRIBUTE`, there is no client
 * manifest to link it). Once hydrated their chunks are loaded and Vite has injected the same CSS, which HMR
 * keeps up to date: the server copy would keep old rules.
 */
export const removeDevLazyCss = () => {
	document.querySelectorAll('style[data-thp-dev-lazy-css]').forEach((style) => style.remove());
};

export const readServerGlobals = () => {
	const data: Partial<Window> = JSON.parse(document.getElementById(MOBIFY_DATA_ELEMENT_ID)?.textContent || '{}');

	Object.assign(window, data);
};

/**
 * React 19 root error callback: errors not caught by an error boundary (`onUncaughtError`) and
 * errors React recovered from, e.g. hydration mismatches (`onRecoverableError`). Errors caught by
 * `ComponentErrorBoundary` are already logged by the boundary itself.
 */
export const createRootErrorLogger =
	(locals: AppLocals, label: string) => (error: unknown, errorInfo?: {componentStack?: string}) => {
		console.error(`[${label}]`, error, errorInfo?.componentStack || '');

		const logProvider = locals.managerRepository?.getLogMgr().getLogProvider();
		const message = error instanceof Error ? error.message : String(error);
		const stack = errorInfo?.componentStack || (error instanceof Error ? error.stack : undefined);

		// Same payload as `ComponentErrorBoundary`: errors raised while rendering the React tree.
		logProvider?.error(`[${label}] ${message} at "${window.location.pathname}"`, LogSource.COMPONENT, {details: stack});
	};

export const fetchInitialMessages = async (fetchTranslations: StartClientOptions['fetchTranslations']) => {
	const preloaded = window.__PRELOADED_STATE__?.messages || {};
	const messages: Record<string, TranslationMessages> = {};

	await Promise.all(
		Object.keys(preloaded).map((lang) =>
			fetchTranslations(preloaded[lang].fetchedLocale).then((result) => {
				messages[lang] = result;
			}),
		),
	);

	delete window.__PRELOADED_STATE__?.messages;

	return messages;
};

const startClient = async ({AppConfig, createQueryClient, fetchTranslations, serviceWorkerUrl = '/worker.js?v=2'}: StartClientOptions) => {
	await documentParsed();
	readServerGlobals();

	const locals: AppLocals = {};
	const envPreferences = window.__ENV_PREFERENCES || {};
	const preloadedState: PreloadedState = window.__PRELOADED_STATE__ || {};

	// AppConfig.restore must run before rendering: it builds the ManagerRepository and Redux store.
	AppConfig.restore(
		locals,
		preloadedState.__STATE_MANAGEMENT_LIBRARY || {},
		{app: {useSLASPrivateClient: window.Progressive?.useSLASPrivateClient}},
		envPreferences,
	);

	// Hydrate the QueryClient BEFORE rendering, so `useSuspenseQuery` finds server data in cache
	// (no `<HydrationBoundary>`: it would hydrate the same state a second time).
	const queryClient = createQueryClient();

	if (preloadedState.__reactQuery) {
		hydrate(queryClient, preloadedState.__reactQuery);
	}

	window.__HYDRATING__ = true;

	// The page is made of lazy chunks (routes → `APIRouter` → page): load the ones rendered on the
	// server before hydrating, like `loadableReady()` on webpack brands, so that their server HTML is
	// hydrated and not rendered again. Their files are already requested by `<link rel="modulepreload">`.
	const [messages] = await Promise.all([
		fetchInitialMessages(fetchTranslations),
		Promise.race([preloadLazyModules(window.__LAZY_MODULES__ || []), wait(LAZY_MODULES_PRELOAD_TIMEOUT)]),
	]);
	let isInitialPage = true;
	const bootstrap: AppBootstrap = {
		locals,
		messages,
		detectedLanguage: preloadedState.detectedLanguageCode,
		envPreferences,
		appProps: preloadedState.appProps || {},
		error: window.__ERROR__,
		correlationId: () => {
			// When hydrating an error page, keep the server correlation id.
			if (isInitialPage && window.__ERROR__) {
				isInitialPage = false;

				return window.__INITIAL_CORRELATION_ID__;
			}

			return uuidv4();
		},
		onHydrate: () => {
			window.__HYDRATING__ = false;

			if (process.env.NODE_ENV === 'development') {
				removeDevLazyCss();
			}

			if (window.__TIMESTAMPS__) {
				window.__TIMESTAMPS__.afterRender = Date.now();
			}
		},
	};

	startTransition(() => {
		hydrateRoot(
			document,
			<QueryClientProvider client={queryClient}>
				<AppBootstrapContext.Provider value={bootstrap}>
					<HydratedRouter />
				</AppBootstrapContext.Provider>
			</QueryClientProvider>,
			{
				onRecoverableError: createRootErrorLogger(locals, 'recoverable error'),
				onUncaughtError: createRootErrorLogger(locals, 'uncaught error'),
			},
		);
	});

	setupServiceWorker(serviceWorkerUrl);
};

export default startClient;
